import { useColorModeValue } from '@ecoinvent/ui';
import { Handle, NodeProps, Position } from '@xyflow/react';
import { FiArrowLeft, FiArrowRight, FiMinus, FiPlus } from 'react-icons/fi';

import { SupplyChainNode as SupplyChainNodeData } from 'hooks/data/datasets/V4/supplyChain/types';

import { COLORS } from './constants';
import { ImpactScoreState } from './ImpactScore';
import SupplyChainCard, { useCardAccent } from './SupplyChainCard';
import { stopEvents } from './utils';

export type NodeVariant = 'root' | 'input' | 'consumer';

export type SupplyChainNodePayload = {
  node: SupplyChainNodeData;
  nodeVariant: NodeVariant;
  isRoot: boolean;
  isActive: boolean;
  // Derived from the edges by the layout, not read off the node: see `functionalChildIds`.
  isFunctional: boolean;
  isExpanded: boolean;
  isOutputsExpanded: boolean;
  inputCount?: number;
  outputCount?: number;
  mode: 'flows' | 'impact_assessment';
  // Resolved by the layout, which derives the vertical pitch from the same value, so the card cannot
  // render at a height the occupancy sweep did not plan for.
  nodeHeight: number;
  // Bulk-resolved score for this node, stamped by the layout; the card renders it and Copy Score reads it.
  scoreStatus: ImpactScoreState['status'];
  score?: number;
  scoreUnit?: string;
  onExpand: () => void;
  onCollapse: () => void;
  onExpandOutputs: () => void;
  onCollapseOutputs: () => void;
  onSelect: () => void;
  onViewDataset: () => void;
  onCopyScore: () => Promise<void>;
};

const invisibleHandle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  width: 1,
  height: 1,
  top: '50%',
};

// Anchored flush to the card edge and shifted by half its own width, so any label length stays centred on the border.
const badgeAnchorStyle = (side: 'left' | 'right'): React.CSSProperties => ({
  position: 'absolute',
  [side]: 0,
  top: '50%',
  transform: `translate(${side === 'left' ? '-50%' : '50%'}, -50%)`,
  display: 'inline-flex',
  zIndex: 10,
});

const badgeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};

const BADGE_ICON_SIZE = 14;

// `display: block` takes the inline SVG off the text baseline so the pill's centring applies to it.
const badgeIconStyle: React.CSSProperties = { display: 'block', flexShrink: 0 };

const badgePillStyle = (backgroundColor: string, color: string): React.CSSProperties => ({
  backgroundColor,
  color,
  borderRadius: '9999px',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: 'inherit',
  lineHeight: 1,
  padding: '0 8px',
  height: '24px',
  minWidth: '33px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
});

// The digits the pill renders and its accessible name read the same count, so the two cannot disagree.
// The count is undefined until the neighbourhood is fetched, and the pill renders no digits then either.
const pillLabel = (isExpanded: boolean, count: number | undefined, noun: 'input' | 'output'): string => {
  const action = isExpanded ? 'Collapse' : 'Expand';
  const subject = count === 1 ? noun : `${noun}s`;
  return count === undefined ? `${action} ${subject}` : `${action} ${count} ${subject}`;
};

// Two-layer badge (Figma): a pale outer disc (same 24px as the expand pill's height, so both read as one
// row on the card border) holding a smaller saturated inner circle, with the arrow on top.
const FLOW_BADGE_SIZE = 24;
// Figma spec verbatim; the ~2.85px pale ring left around it is the intended halo, so no clean-value rounding.
const FLOW_BADGE_INNER_SIZE = 18.29;
const FLOW_BADGE_GAP = 4;
// Feather arrow at this px renders a ~0.83px stroke (default 2 scaled by 10/24), the closest match to
// Figma's 0.86px while keeping the shared react-icons arrow used by the expand pills.
const FLOW_ARROW_SIZE = 10;

// Figma shadow panel, verbatim: a soft two-layer drop under the outer disc.
const FLOW_BADGE_SHADOW = '0 1.5px 3px -0.75px rgba(0,0,0,0.06), 0 3px 4.5px -0.75px rgba(0,0,0,0.10)';

// Anchored past the pill's own outer edge rather than at a fixed offset, so a wider pill pushes the badge
// further out instead of overlapping it. Decorative only: pointer-events off keeps the pill's hit area whole.
const flowBadgeStyle = (side: 'left' | 'right', outerColor: string): React.CSSProperties => ({
  position: 'absolute',
  top: '50%',
  [side === 'right' ? 'left' : 'right']: '100%',
  [side === 'right' ? 'marginLeft' : 'marginRight']: FLOW_BADGE_GAP,
  transform: 'translateY(-50%)',
  width: FLOW_BADGE_SIZE,
  height: FLOW_BADGE_SIZE,
  borderRadius: '9999px',
  backgroundColor: outerColor,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  boxShadow: FLOW_BADGE_SHADOW,
});

// Inner circle ("Base"): the saturated accent, centred inside the outer disc. Also pointer-events off so
// nothing in the decorative badge can become a hit-test target.
const flowBadgeInnerStyle = (innerColor: string, glyphColor: string): React.CSSProperties => ({
  width: FLOW_BADGE_INNER_SIZE,
  height: FLOW_BADGE_INNER_SIZE,
  borderRadius: '9999px',
  backgroundColor: innerColor,
  color: glyphColor,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

type FlowDirectionBadgeProps = {
  // The card's root-facing edge: right for inputs, left for consumers.
  side: 'left' | 'right';
  outerColor: string;
  innerColor: string;
  glyphColor: string;
  isFunctional?: boolean;
};

const BadgeIcon = ({ isExpanded }: { isExpanded: boolean }) =>
  isExpanded ? (
    <FiMinus aria-hidden size={BADGE_ICON_SIZE} style={badgeIconStyle} />
  ) : (
    <FiPlus aria-hidden size={BADGE_ICON_SIZE} style={badgeIconStyle} />
  );

// Flow direction relative to the root: inward on a normal card, outward on the one carrying the root's
// functional flow.
const FlowDirectionBadge = ({ side, outerColor, innerColor, glyphColor, isFunctional }: FlowDirectionBadgeProps) => {
  const pointsRight = (side === 'right') !== Boolean(isFunctional);
  const Arrow = pointsRight ? FiArrowRight : FiArrowLeft;
  return (
    <span aria-hidden style={flowBadgeStyle(side, outerColor)}>
      <div style={flowBadgeInnerStyle(innerColor, glyphColor)}>
        <Arrow size={FLOW_ARROW_SIZE} style={badgeIconStyle} />
      </div>
    </span>
  );
};

const SupplyChainNode = ({ data }: NodeProps) => {
  const {
    node,
    nodeVariant,
    isRoot,
    isActive,
    isFunctional,
    isExpanded,
    isOutputsExpanded,
    inputCount,
    outputCount,
    mode,
    nodeHeight,
    scoreStatus,
    score,
    scoreUnit,
    onExpand,
    onCollapse,
    onExpandOutputs,
    onCollapseOutputs,
    onSelect,
    onViewDataset,
    onCopyScore,
  } = data as SupplyChainNodePayload;

  const expandBg = useColorModeValue(COLORS.expandBgLight, COLORS.expandBgDark);
  const expandColor = useColorModeValue(COLORS.expandGreenLight, COLORS.expandGreenDark);
  const collapseBg = useColorModeValue(COLORS.collapseBgLight, COLORS.collapseBgDark);
  const collapseColor = COLORS.collapseRed;
  const { flowColor, isFunctionalAccent } = useCardAccent({ nodeVariant, isRoot, isActive, isFunctional, mode });
  // Outer disc: one uniform pale/dark pair for every variant (design), reusing the expand pill's halo in light.
  const flowOuterColor = useColorModeValue(COLORS.expandBgLight, COLORS.badgeOuterDark);
  // Arrow: white on the green/purple inners and on the functional inner in light mode; in dark mode the
  // functional inner is near-white (#E7E7E7), so its arrow takes Gray/700 to stay legible.
  const functionalArrowColor = useColorModeValue('#FFFFFF', COLORS.badgeOuterDark);
  const flowGlyphColor = isFunctionalAccent ? functionalArrowColor : '#FFFFFF';

  const hasInputs = inputCount === undefined || inputCount > 0;
  const hasOutputs = outputCount === undefined || outputCount > 0;
  // Impact assessment only, and never on the root: it has no traded product of its own.
  const flowBadgeSide = isRoot || mode !== 'impact_assessment' ? undefined : nodeVariant === 'input' ? 'right' : 'left';
  const flowBadge = flowBadgeSide && (
    <FlowDirectionBadge
      side={flowBadgeSide}
      outerColor={flowOuterColor}
      innerColor={flowColor}
      glyphColor={flowGlyphColor}
      isFunctional={isFunctional}
    />
  );

  return (
    <div className="nopan nodrag" style={{ position: 'relative', cursor: 'default' }}>
      <Handle type="target" position={Position.Left} style={invisibleHandle} />

      {/* Left badges — expand / collapse inputs, plus the flow direction badge on consumer cards */}
      {(hasInputs || flowBadgeSide === 'left') && (
        <div style={badgeAnchorStyle('left')}>
          {hasInputs && (
            <button
              type="button"
              aria-label={pillLabel(isExpanded, inputCount, 'input')}
              aria-expanded={isExpanded}
              className="nopan nodrag"
              style={badgeButtonStyle}
              onPointerDown={stopEvents}
              onMouseDown={stopEvents}
              onClick={(e) => {
                stopEvents(e);
                isExpanded ? onCollapse() : onExpand();
              }}
            >
              <span style={badgePillStyle(isExpanded ? collapseBg : expandBg, isExpanded ? collapseColor : expandColor)}>
                <BadgeIcon isExpanded={isExpanded} />
                {inputCount}
              </span>
            </button>
          )}
          {flowBadgeSide === 'left' && flowBadge}
        </div>
      )}

      <SupplyChainCard
        node={node}
        nodeVariant={nodeVariant}
        isRoot={isRoot}
        isActive={isActive}
        isFunctional={isFunctional}
        mode={mode}
        nodeHeight={nodeHeight}
        scoreStatus={scoreStatus}
        score={score}
        scoreUnit={scoreUnit}
        onSelect={onSelect}
        onViewDataset={onViewDataset}
        onCopyScore={onCopyScore}
      />

      {/* Right badges — expand / collapse outputs, plus the flow direction badge on input cards */}
      {(hasOutputs || flowBadgeSide === 'right') && (
        <div style={badgeAnchorStyle('right')}>
          {hasOutputs && (
            <button
              type="button"
              aria-label={pillLabel(isOutputsExpanded, outputCount, 'output')}
              aria-expanded={isOutputsExpanded}
              className="nopan nodrag"
              style={badgeButtonStyle}
              onPointerDown={stopEvents}
              onMouseDown={stopEvents}
              onClick={(e) => {
                stopEvents(e);
                isOutputsExpanded ? onCollapseOutputs() : onExpandOutputs();
              }}
            >
              <span
                style={badgePillStyle(
                  isOutputsExpanded ? collapseBg : expandBg,
                  isOutputsExpanded ? collapseColor : expandColor
                )}
              >
                <BadgeIcon isExpanded={isOutputsExpanded} />
                {outputCount}
              </span>
            </button>
          )}
          {flowBadgeSide === 'right' && flowBadge}
        </div>
      )}

      <Handle type="source" position={Position.Right} style={invisibleHandle} />
    </div>
  );
};

export default SupplyChainNode;
